import { useState, useEffect } from 'react';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Loader2, Database, CheckCircle, AlertCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

const SyncVehicleData = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [isHistoricalSyncing, setIsHistoricalSyncing] = useState(false);
  const [syncStats, setSyncStats] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Load existing sync stats on component mount
  useEffect(() => {
    const loadStats = async () => {
      setIsLoadingStats(true);
      try {
        const { count: makesCount } = await supabase
          .from('vehicle_makes')
          .select('*', { count: 'exact', head: true });

        const { count: modelsCount } = await supabase
          .from('vehicle_models')
          .select('*', { count: 'exact', head: true });

        const { count: enginesCount } = await supabase
          .from('vehicle_engines')
          .select('*', { count: 'exact', head: true });

        if (makesCount || modelsCount || enginesCount) {
          setSyncStats({
            makesAdded: makesCount || 0,
            modelsAdded: modelsCount || 0,
            enginesAdded: enginesCount || 0
          });
        }
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setIsLoadingStats(false);
      }
    };

    loadStats();
  }, []);

  const startSync = async (isHistorical = false) => {
    if (isHistorical) {
      setIsHistoricalSyncing(true);
    } else {
      setIsSyncing(true);
    }
    setProgress(10);
    setSyncStats(null);

    try {
      const currentYear = new Date().getFullYear();
      let years: number[];
      let background = false;

      if (isHistorical) {
        // Historical years: 1990 to 2015 (run in background)
        years = Array.from({ length: 26 }, (_, i) => 1990 + i);
        background = true;
      } else {
        // Recent years: last 10 years
        years = Array.from({ length: 10 }, (_, i) => currentYear - 9 + i);
      }
      
      // All package types
      const packageTypes = ['Domestic', 'Asian', 'European'];

      setProgress(25);

      console.log('Starting sync with years:', years, 'packages:', packageTypes, 'background:', background);

      const { data, error } = await supabase.functions.invoke('sync-nhtsa-data', {
        body: { years, packageTypes, background }
      });

      setProgress(90);

      if (error) {
        throw error;
      }

      setProgress(100);
      
      if (data.background) {
        // Background sync started
        setSyncStats({ background: true });
        toast({
          title: 'Background Sync Started',
          description: data.message,
        });
      } else {
        // Regular sync completed - update with new stats
        setSyncStats(data.stats);
        toast({
          title: 'Sync Complete!',
          description: data.message,
        });
        
        // Reload stats from database to get accurate counts
        const { count: makesCount } = await supabase
          .from('vehicle_makes')
          .select('*', { count: 'exact', head: true });

        const { count: modelsCount } = await supabase
          .from('vehicle_models')
          .select('*', { count: 'exact', head: true });

        const { count: enginesCount } = await supabase
          .from('vehicle_engines')
          .select('*', { count: 'exact', head: true });

        setSyncStats({
          makesAdded: makesCount || 0,
          modelsAdded: modelsCount || 0,
          enginesAdded: enginesCount || 0
        });
      }

    } catch (error) {
      console.error('Sync error:', error);
      toast({
        title: 'Sync Failed',
        description: error instanceof Error ? error.message : 'Failed to sync vehicle data',
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
      setIsHistoricalSyncing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      
      <div className="container mx-auto p-6 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-6 w-6" />
              Sync Vehicle Database
            </CardTitle>
            <CardDescription>
              Populate your local vehicle database with accurate data from NHTSA. 
              This setup pulls makes, models, and engine configurations 
              for the last 10 years ({new Date().getFullYear() - 9}-{new Date().getFullYear()}) and all packages (Domestic, Asian, European).
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="flex flex-col gap-4">
              <div>
                <h3 className="font-semibold mb-2">Sync Options</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {isLoadingStats
                    ? 'Loading sync status...'
                    : syncStats?.background 
                    ? 'Background sync in progress for historical years...' 
                    : syncStats 
                    ? 'Vehicle data synchronized' 
                    : 'Choose which years to sync'}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Button 
                    onClick={() => startSync(false)} 
                    disabled={isSyncing || isHistoricalSyncing}
                    size="lg"
                    variant="default"
                  >
                    {isSyncing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Database className="mr-2 h-4 w-4" />
                        Sync Recent Years
                      </>
                    )}
                  </Button>

                  <Button 
                    onClick={() => startSync(true)} 
                    disabled={isSyncing || isHistoricalSyncing}
                    size="lg"
                    variant="outline"
                  >
                    {isHistoricalSyncing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <Database className="mr-2 h-4 w-4" />
                        Sync Historical Years
                      </>
                    )}
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <p className="text-xs text-muted-foreground">
                    {new Date().getFullYear() - 9}-{new Date().getFullYear()} • ~60-90 seconds
                  </p>
                  <p className="text-xs text-muted-foreground">
                    1990-2015 • Runs in background (~5-10 min)
                  </p>
                </div>
              </div>

              {(isSyncing || isHistoricalSyncing) && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>{isHistoricalSyncing ? 'Starting background sync...' : 'Syncing vehicle data...'}</span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} />
                </div>
              )}

              {syncStats?.background && (
                <Card className="border-blue-500">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                      Background Sync Running
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Historical years (1990-2015) are being synced in the background. 
                      This may take 5-10 minutes. You can continue using the app while this completes.
                    </p>
                  </CardContent>
                </Card>
              )}

              {syncStats && !syncStats.background && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Makes Added
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{syncStats.makesAdded}</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Models Added
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{syncStats.modelsAdded}</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Engines Added
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{syncStats.enginesAdded}</div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {syncStats && syncStats.errors && syncStats.errors.length > 0 && (
                <Card className="border-yellow-500">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-500" />
                      Warnings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {syncStats.errors.map((error: string, index: number) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {error}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="bg-muted p-4 rounded-lg space-y-2">
              <h4 className="font-semibold text-sm">Sync Strategy</h4>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium">Recent Years ({new Date().getFullYear() - 9}-{new Date().getFullYear()})</p>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside ml-2">
                    <li>Run this 1-2 times per year to get current models</li>
                    <li>Fast sync (~60-90 seconds)</li>
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-medium">Historical Years (1990-2015)</p>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside ml-2">
                    <li>Run once during initial setup</li>
                    <li>Background process (~5-10 minutes)</li>
                    <li>Historical data doesn't change, so one-time sync is sufficient</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SyncVehicleData;
